import {getProductSelection} from '@/product/product-selection';
import {loadProductJson} from '@/product/product-selection/load-product-json';
import type {VariantState} from '@/product/types';
import {createShopifyImageUrl} from '@/utilities/create-shopify-image-url';
import {preloadImage} from '@/utilities/preload-image';
import {h} from 'preact';
import register from 'preact-custom-element';
import ProductImage from './ProductImage';

function loader({className, product: productJson}: ProductImageLoaderProps) {
  loadProductJson(productJson);

  const {product, selectedVariant} = getProductSelection();
  const image = selectedVariant?.image ?? product?.featuredImage;

  // Preload the featured image for each variant, if available
  // This makes the user experience smoother when selecting the variant
  product?.variants.forEach(preloadVariantImage);

  return image
    ? h(ProductImage, {
        image: {
          url: image.url,
          alt: image.alt ?? product?.title ?? 'Product',
        },
        className: className,
      })
    : null;
}

/**
 * Preloads the 'featured' image of a variant.
 * This is either the first image in the variant's images array or the variant's image.
 * Image is preloaded in mobile and desktop widths.
 *
 * @param variant - The variant object containing the image information.
 */
function preloadVariantImage(variant: VariantState) {
  const featured = variant.images[0] ?? variant.image;
  if (!featured) return;

  // Desktop
  preloadImage(createShopifyImageUrl(featured.url, 384));
  // Mobile
  preloadImage(createShopifyImageUrl(featured.url, 128));
}

register(loader, 'product-image', ['product', 'className']);

type ProductImageLoaderProps = {
  className?: string;
  product: string;
};
